/**
 * Thin line icons for the account menu (Turo-style 1.5px strokes).
 * Paths follow the Lucide set (ISC licence), drawn on a 24px grid.
 */
import type { ReactNode, SVGProps } from 'react';

const Svg = ({ children, ...props }: SVGProps<SVGSVGElement> & { children: ReactNode }) => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    {children}
  </svg>
);

export const MenuIcon = () => (
  <Svg>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Svg>
);
export const CloseIcon = () => (
  <Svg>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
);
export const HomeIcon = () => (
  <Svg>
    <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1v-9.5Z" />
  </Svg>
);
export const UserIcon = () => (
  <Svg>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" />
  </Svg>
);
export const CalendarIcon = () => (
  <Svg>
    <rect x="3" y="4.5" width="18" height="16.5" rx="2" />
    <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
  </Svg>
);
export const CalendarXIcon = () => (
  <Svg>
    <rect x="3" y="4.5" width="18" height="16.5" rx="2" />
    <path d="M3 9.5h18M8 2.5v4M16 2.5v4M10 13l4 4M14 13l-4 4" />
  </Svg>
);
export const ChatIcon = () => (
  <Svg>
    <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.9A8 8 0 1 1 21 12Z" />
  </Svg>
);
export const KeyIcon = () => (
  <Svg>
    <circle cx="7.5" cy="15.5" r="4" />
    <path d="m10.5 12.5 9-9M16 7l3 3M14 9l2 2" />
  </Svg>
);
export const AlertIcon = () => (
  <Svg>
    <path d="M10.3 3.9 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4M12 17h.01" />
  </Svg>
);
export const MapPinIcon = () => (
  <Svg>
    <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </Svg>
);
export const ShieldIcon = () => (
  <Svg>
    <path d="M12 21.5s8-3.5 8-10V5l-8-3-8 3v6.5c0 6.5 8 10 8 10Z" />
  </Svg>
);
export const LayersIcon = () => (
  <Svg>
    <path d="m12 2.5 9.5 5-9.5 5-9.5-5 9.5-5Z" />
    <path d="m2.5 12.5 9.5 5 9.5-5M2.5 17l9.5 5 9.5-5" />
  </Svg>
);
export const ReceiptIcon = () => (
  <Svg>
    <path d="M5 2.5h14v19l-3-2-2 2-2-2-2 2-2-2-3 2v-19Z" />
    <path d="M9 7.5h6M9 11.5h6M9 15.5h4" />
  </Svg>
);
export const TagIcon = () => (
  <Svg>
    <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8Z" />
    <circle cx="7.5" cy="7.5" r="1.25" />
  </Svg>
);
export const WalletIcon = () => (
  <Svg>
    <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" />
    <path d="M21 11h-5a2 2 0 0 0 0 4h5v-4Z" />
  </Svg>
);
export const CardIcon = () => (
  <Svg>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </Svg>
);
export const StoreIcon = () => (
  <Svg>
    <path d="M3 9.5 4.5 4h15L21 9.5M3 9.5v11h18v-11M3 9.5h18" />
    <path d="M9.5 20.5v-6h5v6" />
  </Svg>
);
export const HashIcon = () => (
  <Svg>
    <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />
  </Svg>
);
export const GridIcon = () => (
  <Svg>
    <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
    <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
    <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
  </Svg>
);
export const SwapIcon = () => (
  <Svg>
    <path d="M7 4 3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7" />
  </Svg>
);
export const CarIcon = () => (
  <Svg>
    <path d="M5 16.5h14M3.5 16.5V12l2-5.5h13l2 5.5v4.5" />
    <path d="M3.5 12h17" />
    <circle cx="7" cy="16.5" r="2" />
    <circle cx="17" cy="16.5" r="2" />
  </Svg>
);
export const HelpIcon = () => (
  <Svg>
    <circle cx="12" cy="12" r="9.5" />
    <path d="M9.5 9.2a2.6 2.6 0 0 1 5 .9c0 1.7-2.5 2.4-2.5 2.4M12 17h.01" />
  </Svg>
);
export const DocIcon = () => (
  <Svg>
    <path d="M14 2.5H6a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-11l-6-6Z" />
    <path d="M14 2.5v6h6M8 13h8M8 17h5" />
  </Svg>
);
export const LogOutIcon = () => (
  <Svg>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
  </Svg>
);
export const LogInIcon = () => (
  <Svg>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" />
  </Svg>
);
export const UserPlusIcon = () => (
  <Svg>
    <circle cx="9" cy="8" r="4" />
    <path d="M2 21c0-4 3.1-6.5 7-6.5s7 2.5 7 6.5M19 8v6M16 11h6" />
  </Svg>
);
export const StarIcon = () => (
  <Svg>
    <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3l-5.6 2.9 1.1-6.2L3 9.6l6.2-.9L12 3Z" />
  </Svg>
);
export const SettingsIcon = () => (
  <Svg>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2.5v2.2M12 19.3v2.2M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.9 19.1l1.6-1.6M17.5 6.5l1.6-1.6" />
  </Svg>
);
export const MegaphoneIcon = () => (
  <Svg>
    <path d="M3 11v2a1 1 0 0 0 1 1h1.5l7 3.5V6.5L5.5 10H4a1 1 0 0 0-1 1Z" />
    <path d="M12.5 8.5c1.8.4 4.5 1.2 6.5 2.5v2c-2 1.3-4.7 2.1-6.5 2.5" />
    <path d="M6.5 14.5v2.2a1.8 1.8 0 0 0 3.2 1.1l.8-1.1" />
  </Svg>
);
